import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Button,
  makeStyles
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import InternalTicketDialog from "../../components/InternalTicketDialog";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  formatInternalTicketProtocol,
  getInternalTicketPriorityLabel,
  getInternalTicketSlaLabel,
  getInternalTicketStatusLabel
} from "../../helpers/internalTicket";

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === "dark";

  return {
    pagePaper: {
      flex: 1,
      padding: theme.spacing(2.5),
      overflowY: "auto",
      background: isDark
        ? "linear-gradient(180deg, rgba(2, 12, 28, 0.96) 0%, rgba(4, 18, 35, 0.96) 100%)"
        : "radial-gradient(1100px 520px at 85% -8%, #e3f0ee 0%, transparent 52%), radial-gradient(820px 420px at -8% 108%, #e8ecfb 0%, transparent 48%), #eef1f5",
      ...theme.scrollbarStyles
    },
    intro: {
      marginBottom: theme.spacing(2)
    },
    introTitle: {
      fontWeight: 800
    },
    introSubtitle: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5)
    },
    filtersRow: {
      display: "flex",
      gap: theme.spacing(1.25),
      flexWrap: "wrap",
      marginBottom: theme.spacing(2)
    },
    clearButton: {
      alignSelf: "center"
    },
    filterField: {
      minWidth: 180
    },
    quickFiltersRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.25)
    },
    quickFilterChip: {
      borderRadius: 999,
      fontWeight: 700,
      borderColor: isDark ? "rgba(45, 212, 191, 0.40)" : "rgba(20, 184, 166, 0.32)",
      color: isDark ? "#99f6e4" : "#0f766e"
    },
    quickFilterChipActive: {
      color: "#ffffff",
      background: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
      boxShadow: "0 4px 12px -4px rgba(13,148,136,0.5)",
      "&:hover": {
        background: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
        filter: "brightness(1.05)"
      }
    },
    metricsRow: {
      marginBottom: theme.spacing(2)
    },
    metricCard: {
      position: "relative",
      padding: theme.spacing(2),
      borderRadius: 16,
      overflow: "hidden",
      height: "100%",
      background: isDark
        ? "linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.92) 100%)"
        : "#ffffff",
      border: `1px solid ${
        isDark ? "rgba(45, 212, 191, 0.16)" : "rgba(148, 163, 184, 0.18)"
      }`,
      boxShadow: isDark
        ? "0 14px 34px rgba(2, 6, 23, 0.30)"
        : "0 8px 20px -12px rgba(15, 23, 42, 0.25)",
      transition: "transform 0.16s ease, box-shadow 0.16s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: isDark
          ? "0 20px 40px rgba(2, 6, 23, 0.38)"
          : "0 16px 30px -14px rgba(15, 23, 42, 0.30)"
      }
    },
    metricAccent: {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 4
    },
    metricTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: theme.spacing(0.75)
    },
    metricIcon: {
      width: 38,
      height: 38,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0
    },
    metricDesc: {
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
      fontWeight: 500,
      lineHeight: 1.35
    },
    toneTealIcon: {
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "#e6f6f3",
      color: isDark ? "#5eead4" : "#0f766e"
    },
    toneTealAccent: { backgroundColor: "#14b8a6" },
    toneAmberIcon: {
      backgroundColor: isDark ? "rgba(245,158,11,0.18)" : "#fff8eb",
      color: isDark ? "#fbbf24" : "#c98a05"
    },
    toneAmberAccent: { backgroundColor: "#f5b942" },
    toneRoseIcon: {
      backgroundColor: isDark ? "rgba(229,72,77,0.20)" : "#feecec",
      color: isDark ? "#fca5a5" : "#c4292e"
    },
    toneRoseAccent: { backgroundColor: "#e5484d" },
    metricLabel: {
      color: theme.palette.text.secondary,
      fontWeight: 700,
      fontSize: "0.7rem",
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      paddingTop: 3
    },
    metricValue: {
      fontWeight: 800,
      fontSize: "1.95rem",
      letterSpacing: "-0.02em",
      lineHeight: 1.05
    },
    metricValueTotal: {
      color: isDark ? "#2dd4bf" : theme.palette.primary.main
    },
    metricValueSoon: {
      color: isDark ? "#fbbf24" : "#a76b1b"
    },
    metricValueOverdue: {
      color: isDark ? "#f87171" : "#ad4d4d"
    },
    list: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: theme.spacing(2)
    },
    card: {
      padding: theme.spacing(2),
      borderRadius: 16,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.25),
      position: "relative",
      overflow: "hidden",
      cursor: "pointer",
      border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.20)" : "rgba(148, 163, 184, 0.18)"}`,
      background: isDark ? "rgba(8, 20, 38, 0.84)" : "#fff",
      transition:
        "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: isDark
          ? "0 16px 30px rgba(0, 0, 0, 0.36)"
          : "0 14px 28px rgba(15, 23, 42, 0.07)"
      }
    },
    cardOpen: {
      borderColor: isDark ? "rgba(20, 184, 166, 0.34)" : "rgba(23,107,99,0.16)",
      background: isDark
        ? "linear-gradient(180deg, rgba(8, 44, 45, 0.78) 0%, rgba(8, 24, 37, 0.88) 100%)"
        : "linear-gradient(180deg, rgba(23,107,99,0.03) 0%, rgba(255,255,255,0.98) 100%)"
    },
    cardInProgress: {
      borderColor: isDark ? "rgba(96, 165, 250, 0.34)" : "rgba(83,111,176,0.18)",
      background: isDark
        ? "linear-gradient(180deg, rgba(25, 42, 77, 0.76) 0%, rgba(14, 25, 48, 0.90) 100%)"
        : "linear-gradient(180deg, rgba(83,111,176,0.045) 0%, rgba(255,255,255,0.98) 100%)"
    },
    cardWaiting: {
      borderColor: isDark ? "rgba(251, 191, 36, 0.34)" : "rgba(212,138,42,0.18)",
      background: isDark
        ? "linear-gradient(180deg, rgba(82, 53, 16, 0.76) 0%, rgba(50, 34, 12, 0.90) 100%)"
        : "linear-gradient(180deg, rgba(212,138,42,0.05) 0%, rgba(255,255,255,0.98) 100%)"
    },
    cardDone: {
      borderColor: isDark ? "rgba(148, 163, 184, 0.32)" : "rgba(148,163,184,0.18)",
      background: isDark
        ? "linear-gradient(180deg, rgba(51, 65, 85, 0.65) 0%, rgba(22, 30, 45, 0.88) 100%)"
        : "linear-gradient(180deg, rgba(148,163,184,0.06) 0%, rgba(255,255,255,0.98) 100%)"
    },
    cardCancelled: {
      borderColor: isDark ? "rgba(248, 113, 113, 0.34)" : "rgba(181,69,69,0.18)",
      background: isDark
        ? "linear-gradient(180deg, rgba(92, 27, 27, 0.76) 0%, rgba(46, 15, 15, 0.90) 100%)"
        : "linear-gradient(180deg, rgba(181,69,69,0.05) 0%, rgba(255,255,255,0.98) 100%)"
    },
    cardAccent: {
      position: "absolute",
      top: 0,
      left: 0,
      bottom: 0,
      width: 4,
      borderRadius: "16px 0 0 16px"
    },
    cardAccentOpen: {
      background: "linear-gradient(180deg, #2a8b81 0%, #176b63 100%)"
    },
    cardAccentInProgress: {
      background: "linear-gradient(180deg, #6a8fd7 0%, #4f72ba 100%)"
    },
    cardAccentWaiting: {
      background: "linear-gradient(180deg, #d89a3c 0%, #b87920 100%)"
    },
    cardAccentDone: {
      background: "linear-gradient(180deg, #94a3b8 0%, #64748b 100%)"
    },
    cardAccentCancelled: {
      background: "linear-gradient(180deg, #d17a7a 0%, #b45555 100%)"
    },
    cardTop: {
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      alignItems: "flex-start"
    },
    protocol: {
      color: isDark ? "#2dd4bf" : theme.palette.primary.main,
      fontWeight: 700
    },
    cardTitle: {
      fontWeight: 800
    },
    chipsRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(0.75)
    },
    linkButton: {
      alignSelf: "flex-start"
    },
    statusChipOpen: {
      backgroundColor: isDark ? "rgba(20, 184, 166, 0.18)" : "rgba(23,107,99,0.10)",
      color: isDark ? "#5eead4" : "#176b63"
    },
    statusChipInProgress: {
      backgroundColor: isDark ? "rgba(96, 165, 250, 0.18)" : "rgba(83,111,176,0.12)",
      color: isDark ? "#93c5fd" : "#4f72ba"
    },
    statusChipWaiting: {
      backgroundColor: isDark ? "rgba(251, 191, 36, 0.20)" : "rgba(212,138,42,0.14)",
      color: isDark ? "#fcd34d" : "#9a6216"
    },
    statusChipDone: {
      backgroundColor: isDark ? "rgba(148, 163, 184, 0.24)" : "rgba(148,163,184,0.18)",
      color: isDark ? "#e2e8f0" : "#475569"
    },
    statusChipCancelled: {
      backgroundColor: isDark ? "rgba(248, 113, 113, 0.22)" : "rgba(181,69,69,0.12)",
      color: isDark ? "#fecaca" : "#a34040"
    },
    priorityChipLow: {
      backgroundColor: isDark ? "rgba(96, 165, 250, 0.18)" : "rgba(83,111,176,0.10)",
      color: isDark ? "#93c5fd" : "#4f72ba"
    },
    priorityChipMedium: {
      backgroundColor: isDark ? "rgba(20, 184, 166, 0.18)" : "rgba(23,107,99,0.10)",
      color: isDark ? "#5eead4" : "#176b63"
    },
    priorityChipHigh: {
      backgroundColor: isDark ? "rgba(251, 191, 36, 0.20)" : "rgba(212,138,42,0.14)",
      color: isDark ? "#fcd34d" : "#9a6216"
    },
    priorityChipUrgent: {
      backgroundColor: isDark ? "rgba(248, 113, 113, 0.22)" : "rgba(181,69,69,0.12)",
      color: isDark ? "#fecaca" : "#a34040"
    },
    slaChipOnTime: {
      backgroundColor: isDark ? "rgba(96, 165, 250, 0.18)" : "rgba(83,111,176,0.10)",
      color: isDark ? "#93c5fd" : "#4f72ba"
    },
    slaChipDueSoon: {
      backgroundColor: isDark ? "rgba(251, 191, 36, 0.20)" : "rgba(212,138,42,0.14)",
      color: isDark ? "#fcd34d" : "#9a6216"
    },
    slaChipOverdue: {
      backgroundColor: isDark ? "rgba(248, 113, 113, 0.22)" : "rgba(181,69,69,0.12)",
      color: isDark ? "#fecaca" : "#a34040"
    },
    slaChipCompleted: {
      backgroundColor: isDark ? "rgba(148, 163, 184, 0.24)" : "rgba(148,163,184,0.18)",
      color: isDark ? "#e2e8f0" : "#475569"
    },
    slaChipUnscheduled: {
      backgroundColor: isDark ? "rgba(148, 163, 184, 0.20)" : "rgba(148,163,184,0.12)",
      color: isDark ? "#cbd5e1" : "#64748b"
    },
    attentionChip: {
      backgroundColor: isDark ? "rgba(248, 113, 113, 0.22)" : "rgba(181,69,69,0.10)",
      color: isDark ? "#fecaca" : "#a34040"
    },
    emptyState: {
      padding: theme.spacing(4),
      textAlign: "center",
      color: theme.palette.text.secondary,
      background: isDark ? "rgba(15, 23, 42, 0.48)" : "transparent"
    }
  };
});

const getCardStatusClass = (classes, status) => {
  switch (status) {
    case "open":
      return classes.cardOpen;
    case "in_progress":
      return classes.cardInProgress;
    case "waiting":
      return classes.cardWaiting;
    case "done":
      return classes.cardDone;
    case "cancelled":
      return classes.cardCancelled;
    default:
      return "";
  }
};

const getCardAccentClass = (classes, status) => {
  switch (status) {
    case "open":
      return classes.cardAccentOpen;
    case "in_progress":
      return classes.cardAccentInProgress;
    case "waiting":
      return classes.cardAccentWaiting;
    case "done":
      return classes.cardAccentDone;
    case "cancelled":
      return classes.cardAccentCancelled;
    default:
      return classes.cardAccentOpen;
  }
};

const getStatusChipClass = (classes, status) => {
  switch (status) {
    case "open":
      return classes.statusChipOpen;
    case "in_progress":
      return classes.statusChipInProgress;
    case "waiting":
      return classes.statusChipWaiting;
    case "done":
      return classes.statusChipDone;
    case "cancelled":
      return classes.statusChipCancelled;
    default:
      return "";
  }
};

const getPriorityChipClass = (classes, priority) => {
  switch (priority) {
    case "low":
      return classes.priorityChipLow;
    case "medium":
      return classes.priorityChipMedium;
    case "high":
      return classes.priorityChipHigh;
    case "urgent":
      return classes.priorityChipUrgent;
    default:
      return "";
  }
};

const getSlaChipClass = (classes, slaStatus) => {
  switch (slaStatus) {
    case "on_time":
      return classes.slaChipOnTime;
    case "due_soon":
      return classes.slaChipDueSoon;
    case "overdue":
      return classes.slaChipOverdue;
    case "completed":
      return classes.slaChipCompleted;
    case "unscheduled":
      return classes.slaChipUnscheduled;
    default:
      return "";
  }
};

const InternalTickets = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    slaStatus: "",
    searchParam: "",
    assignedScope: ""
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/internal-tickets", {
        params: {
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.slaStatus ? { slaStatus: filters.slaStatus } : {}),
          ...(filters.searchParam ? { searchParam: filters.searchParam } : {}),
          ...(filters.assignedScope
            ? { assignedScope: filters.assignedScope }
            : {})
        }
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("internalTicket", () => {
      fetchTickets();
    });

    return () => {
      socket.disconnect();
    };
  }, [fetchTickets]);

  const metrics = useMemo(
    () => ({
      total: items.length,
      dueSoon: items.filter(item => item.slaStatus === "due_soon").length,
      overdue: items.filter(item => item.slaStatus === "overdue").length
    }),
    [items]
  );

  const hasActiveFilters = Boolean(
    filters.status ||
      filters.slaStatus ||
      filters.searchParam ||
      filters.assignedScope
  );

  const handleFilterChange = field => event => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleToggleAssignedScope = scope => {
    setFilters(prev => ({
      ...prev,
      assignedScope: prev.assignedScope === scope ? "" : scope
    }));
  };

  const openInternalTicket = useCallback(
    item => {
      if (!item?.ticketId) return;
      history.push(`/tickets/${item.ticketId}?internalTicketId=${item.id}`);
    },
    [history]
  );

  const openInternalTicketEditor = useCallback(item => {
    setSelectedTicket(item);
    setDialogOpen(true);
  }, []);

  return (
    <MainContainer>
      <InternalTicketDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        ticketId={selectedTicket?.ticketId}
        contactId={
          selectedTicket?.contactId ||
          selectedTicket?.contact?.id ||
          selectedTicket?.ticket?.contact?.id
        }
        internalTicket={selectedTicket}
        onSaved={fetchTickets}
      />

      <MainHeader>
        <Title>{i18n.t("internalTickets.page.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSelectedTicket(null);
              setDialogOpen(true);
            }}
          >
            {i18n.t("internalTickets.page.buttons.new")}
          </Button>
          <Button variant="outlined" color="primary" onClick={fetchTickets}>
            {i18n.t("internalTickets.page.buttons.refresh")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.pagePaper} variant="outlined">
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={260}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <div className={classes.intro}>
              <Typography variant="h5" className={classes.introTitle}>
                {i18n.t("internalTickets.page.intro.title")}
              </Typography>
              <Typography variant="body2" className={classes.introSubtitle}>
                {i18n.t("internalTickets.page.intro.subtitle")}
              </Typography>
            </div>

            <Grid container spacing={2} className={classes.metricsRow}>
              <Grid item xs={12} md={4}>
                <Paper elevation={0} className={classes.metricCard}>
                  <span
                    className={`${classes.metricAccent} ${classes.toneTealAccent}`}
                  />
                  <div className={classes.metricTop}>
                    <Typography className={classes.metricLabel}>
                      {i18n.t("internalTickets.page.metrics.total")}
                    </Typography>
                    <span
                      className={`${classes.metricIcon} ${classes.toneTealIcon}`}
                    >
                      <AssignmentOutlinedIcon />
                    </span>
                  </div>
                  <Typography
                    className={`${classes.metricValue} ${classes.metricValueTotal}`}
                  >
                    {metrics.total}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper elevation={0} className={classes.metricCard}>
                  <span
                    className={`${classes.metricAccent} ${classes.toneAmberAccent}`}
                  />
                  <div className={classes.metricTop}>
                    <Typography className={classes.metricLabel}>
                      {i18n.t("internalTickets.page.metrics.dueSoon")}
                    </Typography>
                    <span
                      className={`${classes.metricIcon} ${classes.toneAmberIcon}`}
                    >
                      <ScheduleOutlinedIcon />
                    </span>
                  </div>
                  <Typography
                    className={`${classes.metricValue} ${classes.metricValueSoon}`}
                  >
                    {metrics.dueSoon}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper elevation={0} className={classes.metricCard}>
                  <span
                    className={`${classes.metricAccent} ${classes.toneRoseAccent}`}
                  />
                  <div className={classes.metricTop}>
                    <Typography className={classes.metricLabel}>
                      {i18n.t("internalTickets.page.metrics.overdue")}
                    </Typography>
                    <span
                      className={`${classes.metricIcon} ${classes.toneRoseIcon}`}
                    >
                      <ErrorOutlineOutlinedIcon />
                    </span>
                  </div>
                  <Typography
                    className={`${classes.metricValue} ${classes.metricValueOverdue}`}
                  >
                    {metrics.overdue}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <div className={classes.filtersRow}>
              <TextField
                className={classes.filterField}
                variant="outlined"
                size="small"
                label={i18n.t("internalTickets.page.filters.search")}
                value={filters.searchParam}
                onChange={handleFilterChange("searchParam")}
              />
              <TextField
                select
                className={classes.filterField}
                variant="outlined"
                size="small"
                label={i18n.t("internalTickets.page.filters.status")}
                value={filters.status}
                onChange={handleFilterChange("status")}
              >
                <MenuItem value="">{i18n.t("internalTickets.page.filters.allActive")}</MenuItem>
                <MenuItem value="open">{i18n.t("internalTickets.labels.status.open")}</MenuItem>
                <MenuItem value="in_progress">{i18n.t("internalTickets.labels.status.in_progress")}</MenuItem>
                <MenuItem value="waiting">{i18n.t("internalTickets.labels.status.waiting")}</MenuItem>
                <MenuItem value="done">{i18n.t("internalTickets.labels.status.done")}</MenuItem>
                <MenuItem value="cancelled">{i18n.t("internalTickets.labels.status.cancelled")}</MenuItem>
              </TextField>
              <TextField
                select
                className={classes.filterField}
                variant="outlined"
                size="small"
                label="SLA"
                value={filters.slaStatus}
                onChange={handleFilterChange("slaStatus")}
              >
                <MenuItem value="">{i18n.t("internalTickets.page.filters.all")}</MenuItem>
                <MenuItem value="on_time">{i18n.t("internalTickets.labels.sla.on_time")}</MenuItem>
                <MenuItem value="due_soon">{i18n.t("internalTickets.labels.sla.due_soon")}</MenuItem>
                <MenuItem value="overdue">{i18n.t("internalTickets.labels.sla.overdue")}</MenuItem>
                <MenuItem value="completed">{i18n.t("internalTickets.labels.sla.completed")}</MenuItem>
                <MenuItem value="unscheduled">{i18n.t("internalTickets.labels.sla.unscheduled")}</MenuItem>
              </TextField>
              <div className={classes.quickFiltersRow}>
                <Chip
                  clickable
                  label={i18n.t("internalTickets.page.filters.mine")}
                  variant={
                    filters.assignedScope === "mine" ? "default" : "outlined"
                  }
                  color={
                    filters.assignedScope === "mine" ? "primary" : "default"
                  }
                  className={`${classes.quickFilterChip} ${
                    filters.assignedScope === "mine"
                      ? classes.quickFilterChipActive
                      : ""
                  }`}
                  onClick={() => handleToggleAssignedScope("mine")}
                />
                <Chip
                  clickable
                  label={i18n.t("internalTickets.page.filters.unassigned")}
                  variant={
                    filters.assignedScope === "unassigned"
                      ? "default"
                      : "outlined"
                  }
                  color={
                    filters.assignedScope === "unassigned"
                      ? "primary"
                      : "default"
                  }
                  className={`${classes.quickFilterChip} ${
                    filters.assignedScope === "unassigned"
                      ? classes.quickFilterChipActive
                      : ""
                  }`}
                  onClick={() => handleToggleAssignedScope("unassigned")}
                />
              </div>
              {hasActiveFilters && (
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  className={classes.clearButton}
                  onClick={() =>
                    setFilters({
                      status: "",
                      slaStatus: "",
                      searchParam: "",
                      assignedScope: ""
                    })
                  }
                >
                  {i18n.t("internalTickets.page.buttons.clearFilters")}
                </Button>
              )}
            </div>

            {items.length ? (
              <div className={classes.list}>
                {items.map(item => (
                  <Paper
                    variant="outlined"
                    className={`${classes.card} ${getCardStatusClass(
                      classes,
                      item.status
                    )}`}
                    key={item.id}
                    onClick={() => openInternalTicketEditor(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openInternalTicketEditor(item);
                      }
                    }}
                  >
                    <div
                      className={`${classes.cardAccent} ${getCardAccentClass(
                        classes,
                        item.status
                      )}`}
                    />

                    <div className={classes.cardTop}>
                      <div>
                        <Typography variant="body2" className={classes.protocol}>
                          {formatInternalTicketProtocol(item)}
                        </Typography>
                        <Typography className={classes.cardTitle}>
                          {item.title}
                        </Typography>
                      </div>
                      <Chip
                        size="small"
                        label={getInternalTicketStatusLabel(item.status)}
                        className={getStatusChipClass(classes, item.status)}
                      />
                    </div>

                    <Typography variant="body2" color="textSecondary">
                      {item.description || i18n.t("internalTickets.page.card.noDescription")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item.ticketId
                        ? `${i18n.t("internalTickets.page.card.linkedTicket")}: #${String(item.ticketId).padStart(6, "0")} - ${
                            item.ticket?.contact?.name ||
                            item.contact?.name ||
                            "-"
                          }`
                        : item.contact?.name
                        ? `${i18n.t("internalTickets.page.card.linkedClient")}: ${item.contact.name}`
                        : i18n.t("internalTickets.page.card.noLink")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("internalTickets.page.card.responsible")}: {item.assignedUser?.name || i18n.t("internalTickets.page.card.notDefined")}
                    </Typography>
                    {item.slaDueAt ? (
                      <Typography variant="body2" color="textSecondary">
                        SLA: {new Date(item.slaDueAt).toLocaleString("pt-BR")}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        SLA: {i18n.t("internalTickets.page.card.slaUnset")}
                      </Typography>
                    )}

                    <div className={classes.chipsRow}>
                      <Chip
                        size="small"
                        icon={<AssignmentOutlinedIcon fontSize="small" />}
                        label={getInternalTicketPriorityLabel(item.priority)}
                        className={getPriorityChipClass(classes, item.priority)}
                      />
                      <Chip
                        size="small"
                        icon={<ScheduleOutlinedIcon fontSize="small" />}
                        label={getInternalTicketSlaLabel(item.slaStatus)}
                        className={getSlaChipClass(classes, item.slaStatus)}
                      />
                      {item.slaStatus === "overdue" ? (
                        <Chip
                          size="small"
                          icon={<ErrorOutlineOutlinedIcon fontSize="small" />}
                          label={i18n.t("internalTickets.page.card.attention")}
                          className={classes.attentionChip}
                        />
                      ) : null}
                    </div>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={1}
                    >
                      {item.ticketId ? (
                        <Button
                          className={classes.linkButton}
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={event => {
                            event.stopPropagation();
                            openInternalTicket(item);
                          }}
                        >
                          {i18n.t("internalTickets.page.buttons.openAttendance")}
                        </Button>
                      ) : (
                        <span />
                      )}
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        onClick={event => {
                          event.stopPropagation();
                          openInternalTicketEditor(item);
                        }}
                      >
                        {i18n.t("internalTickets.page.buttons.edit")}
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </div>
            ) : (
              <Paper variant="outlined" className={classes.emptyState}>
                {hasActiveFilters
                  ? i18n.t("internalTickets.page.empty.filtered")
                  : i18n.t("internalTickets.page.empty.default")}
              </Paper>
            )}
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default InternalTickets;

